/**
 * Redux slice for system health state with built-in caching.
 *
 * - Fetches from /api/v1/system/health
 * - Caches result for CACHE_TTL_MS (30 s) to avoid redundant calls
 * - Provides selectors for overall status, per-module, critical checks
 */
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from './index';
import systemHealthService from '../services/systemHealthService';
import type { SystemHealthResponse, ServiceStatus } from '../services/systemHealthService';

const CACHE_TTL_MS = 30_000; // 30 seconds

// ── State ────────────────────────────────────────────────────────────

export interface SystemHealthState {
  data: SystemHealthResponse | null;
  loading: boolean;
  error: string | null;
  lastFetchedAt: number | null; // epoch ms
}

const initialState: SystemHealthState = {
  data: null,
  loading: false,
  error: null,
  lastFetchedAt: null,
};

// ── Thunk ────────────────────────────────────────────────────────────

export const fetchSystemHealth = createAsyncThunk<
  SystemHealthResponse,
  { force?: boolean } | undefined,
  { state: RootState }
>(
  'systemHealth/fetch',
  async () => {
    return await systemHealthService.fetchSystemHealth();
  },
  {
    condition: (arg, { getState }) => {
      const { systemHealth } = getState();
      // Skip if already loading
      if (systemHealth.loading) return false;
      // Skip if cache is fresh (unless forced)
      if (arg?.force) return true;
      if (
        systemHealth.lastFetchedAt &&
        Date.now() - systemHealth.lastFetchedAt < CACHE_TTL_MS
      ) {
        return false;
      }
      return true;
    },
  },
);

// ── Slice ────────────────────────────────────────────────────────────

const systemHealthSlice = createSlice({
  name: 'systemHealth',
  initialState,
  reducers: {
    clearHealth(state) {
      state.data = null;
      state.error = null;
      state.lastFetchedAt = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSystemHealth.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSystemHealth.fulfilled, (state, action: PayloadAction<SystemHealthResponse>) => {
        state.data = action.payload;
        state.loading = false;
        state.lastFetchedAt = Date.now();
      })
      .addCase(fetchSystemHealth.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? 'Failed to fetch system health';
      });
  },
});

export const { clearHealth } = systemHealthSlice.actions;

// ── Selectors ────────────────────────────────────────────────────────

export const selectSystemHealth = (state: RootState) => state.systemHealth.data;
export const selectHealthLoading = (state: RootState) => state.systemHealth.loading;
export const selectHealthError = (state: RootState) => state.systemHealth.error;

export const selectOverallStatus = (state: RootState): ServiceStatus =>
  state.systemHealth.data?.overall_status ?? 'unknown';

export const selectModuleHealth = (state: RootState, moduleKey: string) =>
  state.systemHealth.data?.modules?.[moduleKey] ?? null;

/** True when a critical module (database) is down — blocks login. */
export const selectDatabaseDown = (state: RootState): boolean => {
  const infra = state.systemHealth.data?.modules?.infrastructure;
  if (!infra) return false;
  const db = infra.providers?.find((p) => p.provider === 'database');
  return db?.status === 'down';
};

/** True when any critical module is degraded (STT/LLM down). */
export const selectCriticalDegraded = (state: RootState): boolean => {
  const data = state.systemHealth.data;
  if (!data) return false;
  return (data.down_services?.length ?? 0) > 0 || (data.degraded_services?.length ?? 0) > 0;
};

export default systemHealthSlice.reducer;
